// Labeler Annotation Module - Vuex store for annotation state management
import { annotationService } from '@/services/annotation.service'

export const labelerAnnotation = {
  namespaced: true,

  state: {
    // Drawing state
    drawingMode: null, // 'box' | 'freehand' | null
    isDrawing: false,
    currentAnnotation: null,
    selectedAnnotationId: null,
    selectedPathologyForDrawing: null, // Pathology selected before drawing
    selectedPathologyColorForDrawing: '#ed1a4f', // Color for selected pathology
    editingAnnotationId: null, // Annotation being edited
    isEditMode: false, // Whether in edit mode

    // Annotation data
    annotations: [], // Current image annotations
    allAnnotations: {}, // All annotations keyed by imageId

    // Project config
    pathologyOptions: [],
    diagnosisOptions: [],
    tagOptions: [],
    freehandEnabled: true,
    aiEnabled: true,

    // Selected diagnoses and tags for current image
    selectedDiagnoses: [],
    selectedTags: [],
    comments: '',

    // UI state
    hasUnsavedChanges: false,
    isLoading: false,
    aiLoading: false,
    error: null
  },

  getters: {
    canSave: (state) => {
      return state.annotations.length > 0 || state.selectedDiagnoses.includes('Normal')
    },
    allAnnotationsLabeled: (state) => {
      return state.annotations.every(ann => ann.pathology !== null)
    },
    unlabeledAnnotationsCount: (state) => {
      return state.annotations.filter(ann => !ann.pathology).length
    }
  },

  mutations: {
    SET_DRAWING_MODE (state, mode) {
      state.drawingMode = mode
    },
    SET_IS_DRAWING (state, isDrawing) {
      state.isDrawing = isDrawing
    },
    SET_CURRENT_ANNOTATION (state, annotation) {
      state.currentAnnotation = annotation
    },
    SET_SELECTED_ANNOTATION (state, annotationId) {
      state.selectedAnnotationId = annotationId
    },
    SET_SELECTED_PATHOLOGY_FOR_DRAWING (state, { pathology, color }) {
      state.selectedPathologyForDrawing = pathology
      state.selectedPathologyColorForDrawing = color
    },
    SET_EDITING_ANNOTATION (state, annotationId) {
      state.editingAnnotationId = annotationId
      state.isEditMode = annotationId !== null
      // Disable drawing mode when editing
      if (annotationId !== null) {
        state.drawingMode = null
      }
    },
    ADD_ANNOTATION (state, annotation) {
      // Auto-assign the selected pathology to new annotations
      if (state.selectedPathologyForDrawing && !annotation.pathology) {
        annotation.pathology = state.selectedPathologyForDrawing
        annotation.color = state.selectedPathologyColorForDrawing
      }
      state.annotations.push(annotation)
      state.hasUnsavedChanges = true
    },
    UPDATE_ANNOTATION (state, { annotationId, updates }) {
      const annotation = state.annotations.find(a => a.id === annotationId)
      if (annotation) {
        Object.assign(annotation, updates)
        state.hasUnsavedChanges = true
      }
    },
    DELETE_ANNOTATION (state, annotationId) {
      state.annotations = state.annotations.filter(a => a.id !== annotationId)
      state.hasUnsavedChanges = true
    },
    SET_PATHOLOGY (state, { annotationId, pathology, color }) {
      const annotation = state.annotations.find(a => a.id === annotationId)
      if (annotation) {
        annotation.pathology = pathology
        annotation.color = color
        state.hasUnsavedChanges = true
      }
    },
    SET_PROJECT_CONFIG (state, config) {
      state.pathologyOptions = config.pathologies || []
      state.diagnosisOptions = config.diagnosis || []
      state.tagOptions = config.tags || []
      state.freehandEnabled = config.freehand_enabled ?? true
      state.aiEnabled = config.ai_enabled ?? true
    },
    SET_SELECTED_DIAGNOSES (state, diagnoses) {
      state.selectedDiagnoses = diagnoses
      state.hasUnsavedChanges = true
    },
    SET_SELECTED_TAGS (state, tags) {
      state.selectedTags = tags
      state.hasUnsavedChanges = true
    },
    SET_COMMENTS (state, comments) {
      state.comments = comments
      state.hasUnsavedChanges = true
    },
    CLEAR_ANNOTATIONS (state) {
      state.annotations = []
      state.selectedDiagnoses = []
      state.selectedTags = []
      state.comments = ''
      state.hasUnsavedChanges = false
      state.selectedAnnotationId = null
    },
    SET_LOADING (state, isLoading) {
      state.isLoading = isLoading
    },
    SET_AI_LOADING (state, isLoading) {
      state.aiLoading = isLoading
    },
    SET_ERROR (state, error) {
      state.error = error
    },
    SET_ANNOTATIONS (state, annotations) {
      state.annotations = annotations
      state.hasUnsavedChanges = false
    }
  },

  actions: {
    startDrawing ({ commit }, { mode, startPoint }) {
      commit('SET_DRAWING_MODE', mode)
      commit('SET_IS_DRAWING', true)
      commit('SET_CURRENT_ANNOTATION', { startPoint })
    },

    finishDrawing ({ commit }, annotation) {
      commit('ADD_ANNOTATION', annotation)
      commit('SET_IS_DRAWING', false)
      commit('SET_CURRENT_ANNOTATION', null)
      // Reset pathology selection and drawing mode after finishing
      commit('SET_SELECTED_PATHOLOGY_FOR_DRAWING', { pathology: null, color: '#ed1a4f' })
      commit('SET_DRAWING_MODE', null)
    },

    selectAnnotation ({ commit }, annotationId) {
      commit('SET_SELECTED_ANNOTATION', annotationId)
    },

    updateAnnotation ({ commit }, { annotationId, updates }) {
      commit('UPDATE_ANNOTATION', { annotationId, updates })
    },

    deleteAnnotation ({ commit }, annotationId) {
      commit('DELETE_ANNOTATION', annotationId)
    },

    setPathology ({ commit }, { annotationId, pathology, color }) {
      commit('SET_PATHOLOGY', { annotationId, pathology, color })
    },

    async fetchProjectConfig ({ commit }, projectId) {
      try {
        commit('SET_LOADING', true)
        const config = await annotationService.getProjectConfig(projectId)
        commit('SET_PROJECT_CONFIG', config)
        commit('SET_ERROR', null)
      } catch (error) {
        console.error('Failed to fetch project config:', error)
        commit('SET_ERROR', error.message || 'Failed to fetch project configuration')
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async saveAnnotations ({ commit, state, rootState }, { imageId, projectId }) {
      try {
        commit('SET_LOADING', true)

        // Get current image from labelerImageQueue module
        const currentImage = rootState.labelerImageQueue?.currentImage
        const sopInstanceUID = currentImage?.dicom?.SOPInstanceUID || null

        const labelData = {
          image: imageId,
          diagnosis: state.selectedDiagnoses[0] || '',
          diagnoses: state.selectedDiagnoses,
          tags: state.selectedTags,
          pathologies: state.annotations.map(ann => ({
            coords: ann.coords || undefined,
            name: ann.pathology,
            color: ann.color,
            freehandpoints: ann.freehandpoints || [],
            planes: ann.planes || {},
            z: ann.z !== undefined && ann.z !== null ? ann.z : null,
            other: ann.other || [],
            can3dSegment: ann.can3dSegment || false,
            window: ann.window || null,
            mapped_stats: ann.mapped_stats || {},
            autoSegment: ann.autoSegment || [],
            instanceId: ann.instanceId ? ann.instanceId : sopInstanceUID
          })),
          comments: state.comments,
          projectId
        }

        await annotationService.saveLabel(imageId, labelData)
        commit('CLEAR_ANNOTATIONS')
        commit('SET_ERROR', null)
        return true
      } catch (error) {
        console.error('Failed to save annotations:', error)
        commit('SET_ERROR', error.message || 'Failed to save annotations')
        return false
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchAILabels ({ commit, state }, { imageId, projectId }) {
      try {
        commit('SET_AI_LOADING', true)
        const aiLabels = await annotationService.getAILabels(imageId, projectId)

        // Transform AI labels to annotations
        aiLabels.forEach(label => {
          const annotation = {
            id: crypto.randomUUID(),
            type: label.freehandpoints ? 'freehand' : 'box',
            coords: label.coords,
            freehandpoints: label.freehandpoints,
            pathology: label.name,
            color: label.color,
            isAI: true,
            confidence: label.confidence
          }
          commit('ADD_ANNOTATION', annotation)
        })

        commit('SET_ERROR', null)
      } catch (error) {
        console.error('Failed to fetch AI labels:', error)
        commit('SET_ERROR', error.message || 'Failed to fetch AI labels')
      } finally {
        commit('SET_AI_LOADING', false)
      }
    },

    clearAnnotations ({ commit }) {
      commit('CLEAR_ANNOTATIONS')
    }
  }
}
